import React, { useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputTextarea } from 'primereact/inputtextarea';
import { Button } from 'primereact/button';
import { toast } from 'react-toastify';
import { bidAnalysisServices } from 'services/bidAnalysis/bidAnalysisServices';
import styles from './ApproveConfirmationDialog.module.scss';

interface ApproveConfirmationDialogProps {
  visible: boolean;
  onHide: () => void;
  bidId: number;
  onSuccess: () => void;
  bidStatus?: string;
}

const ApproveConfirmationDialog: React.FC<ApproveConfirmationDialogProps> = ({
  visible,
  onHide,
  bidId,
  onSuccess,
  bidStatus,
}) => {
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleCancel = () => {
    setNotes('');
    onHide();
  };

  const handleApprove = async () => {
    // Check if bid is already approved
    if (bidStatus?.toLowerCase() === 'approved') {
      toast.error('This bid has already been approved');
      return;
    }

    setIsSubmitting(true);
    try {
      await bidAnalysisServices.markApproved(bidId, notes.trim() || '');

      toast.success('Bid approved successfully');
      handleCancel();
      onSuccess();
    } catch (error: any) {
      console.error('Error approving bid:', error);
      toast.error(error?.response?.data?.message || 'Failed to approve bid. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      header="Approve Bid"
      visible={visible}
      onHide={handleCancel}
      style={{ width: '50vw', maxWidth: '500px' }}
      breakpoints={{ '960px': '75vw', '641px': '90vw' }}
      className={styles.dialog}
      closable={true}
      closeOnEscape={true}
    >
      <div className={styles.dialogContent}>
        <div className={styles.confirmationMessage}>
          <i className="pi pi-question-circle" style={{ fontSize: '2rem', color: '#522d6e', marginBottom: '1rem' }}></i>
          <p>Are you sure you want to approve this bid?</p>
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="approvalNotes" className={styles.label}>
            Notes (Optional)
          </label>
          <InputTextarea
            id="approvalNotes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add any additional notes..."
            rows={4}
            className={styles.textarea}
            autoResize
          />
        </div>

        <div className={styles.buttonGroup}>
          <Button
            label="Cancel"
            icon="pi pi-times"
            className={styles.cancelButton}
            onClick={handleCancel}
            disabled={isSubmitting}
          />
          <Button
            label={isSubmitting ? 'Approving...' : 'Approve'}
            icon={isSubmitting ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
            className={styles.approveButton}
            onClick={handleApprove}
            disabled={isSubmitting}
          />
        </div>
      </div>
    </Dialog>
  );
};

export default ApproveConfirmationDialog;

